package co.maytes.standupbot.dashboard.pages.app.standups

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBox
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.motion.Revalidating
import co.maytes.standupbot.dashboard.components.motion.Staggered
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.standups.StandupCard
import co.maytes.standupbot.dashboard.features.standupslist.StandupsListContainer
import co.maytes.standupbot.dashboard.features.standupslist.StandupsListContract
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.px

@Page("/app/standups")
@Composable
fun TeamsIndexPage() = AuthGuard {
    AppShell {
        StandupsListScreen()
    }
}

@Composable
private fun StandupsListScreen() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val container = remember(api) { StandupsListContainer(scope, api) }

    val state by container.subscribe { action ->
        when (action) {
            is StandupsListContract.Action.Navigate -> ctx.router.navigateTo(action.path)
        }
    }

    val workspaceId = auth.currentUser?.workspace?.id
    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            container.intent(StandupsListContract.Intent.Load(workspaceId))
        }
    }

    StandupsListView(state) { container.intent(it) }
}

@Composable
private fun StandupsListView(
    state: StandupsListContract.State,
    dispatch: (StandupsListContract.Intent) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(Layout.wide),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppPageHeader(
                eyebrow = "workspace",
                title = "Standups",
                action = {
                    Button(
                        label = "+ New standup",
                        variant = ButtonVariant.Primary,
                        onClick = { dispatch(StandupsListContract.Intent.Create) },
                    )
                },
            )

            StateSwap(state = state.phase) {
                when (val s = state.status) {
                    StandupsListContract.Status.Idle, StandupsListContract.Status.Loading -> SkeletonGrid()
                    is StandupsListContract.Status.Error -> ErrorState(
                        message = s.error.displayMessage,
                        onRetry = { dispatch(StandupsListContract.Intent.Retry) },
                    )
                    StandupsListContract.Status.Ready,
                    StandupsListContract.Status.Refreshing,
                    -> Revalidating(refreshing = s == StandupsListContract.Status.Refreshing) {
                        if (state.isEmpty) {
                            EmptyStandupsState(
                                onCreate = { dispatch(StandupsListContract.Intent.Create) },
                            )
                        } else {
                            org.jetbrains.compose.web.dom.Div(
                                attrs = Modifier
                                    .fillMaxWidth()
                                    .styleModifier {
                                        property("display", "grid")
                                        property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                                        property("gap", "${GRID_GAP_PX}px")
                                    }
                                    .toAttrs(),
                            ) {
                                Staggered(items = state.items, key = { it.id }, animate = !state.hasEntered) { standup ->
                                    StandupCard(
                                        standup = standup,
                                        onClick = {
                                            dispatch(StandupsListContract.Intent.OpenDetail(standup.id))
                                        },
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SkeletonGrid() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                property("gap", "${GRID_GAP_PX}px")
            },
    ) {
        repeat(SKELETON_COUNT) {
            SkeletonStandupCard()
        }
    }
}

@Composable
private fun SkeletonStandupCard() {
    SkeletonCard(modifier = Modifier.styleModifier { property("gap", "16px") }) {
        com.varabyte.kobweb.compose.foundation.layout.Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = com.varabyte.kobweb.compose.ui.Alignment.CenterVertically,
            horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.SpaceBetween,
        ) {
            SkeletonLine(widthPct = 52, heightPx = 16)
            SkeletonBox(widthPx = 56, heightPx = 22, radiusPx = 999)
        }
        com.varabyte.kobweb.compose.foundation.layout.Row(
            modifier = Modifier.styleModifier { property("gap", "8px") },
        ) {
            SkeletonBox(widthPx = 92, heightPx = 22, radiusPx = 999)
            SkeletonBox(widthPx = 70, heightPx = 22, radiusPx = 999)
        }
        SkeletonLine(widthPct = 70, heightPx = 12)
        SkeletonLine(widthPct = 44, heightPx = 11)
    }
}

@Composable
private fun EmptyStandupsState(onCreate: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background-color", Palette.surfaceSubtle.toString())
                property("border-radius", "${Radius.lg.value}px")
                property("border", "1px dashed ${Palette.border}")
                property("padding", "24px")
                property("transition", Motion.TRANSITION_BASE)
            },
    ) {
        EmptyState(
            title = "Create your first standup",
            subtitle = "Pick a Slack channel and schedule — StandupBot pulls each member's Linear activity, writes the update with Claude, and posts it automatically.",
            icon = { LucideMessagesSquare(size = 26) },
            ctaLabel = "+ Create your first standup",
            onCta = onCreate,
        )
    }
}

private const val PAGE_MAX_WIDTH = 1280
private const val GRID_GAP_PX = 24
private const val SKELETON_COUNT = 3
